import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@promptwatch/ui/components/button";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@promptwatch/ui/components/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@promptwatch/ui/components/form";
import { Input } from "@promptwatch/ui/components/input";
import { Textarea } from "@promptwatch/ui/components/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { jobs } from "@/pages/careers";
import { trpc } from "@/utils/trpc";

const ApplySchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  heardAbout: z.string().min(1, { message: "Please let us know how you heard about us" }),
  motivation: z.string().min(1, { message: "Motivation is required" }),
  linkedinUrl: z.union([z.string().url({ message: "Must be a valid URL" }), z.literal("")]).optional(),
  githubUrl: z.union([z.string().url({ message: "Must be a valid URL" }), z.literal("")]).optional(),
  portfolioUrl: z.union([z.string().url({ message: "Must be a valid URL" }), z.literal("")]).optional(),
});

type ApplyValues = z.infer<typeof ApplySchema>;

interface ApplyModalProps {
  jobId: string;
  onSuccess?: () => void;
}

const ApplyModal = ({ jobId, onSuccess }: ApplyModalProps) => {
  const posthog = usePostHog();
  const [step, setStep] = useState<"apply" | "success">("apply");
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  // Find the job by ID
  const job = jobs.find((j) => j.id === jobId);

  const applyForm = useForm<ApplyValues>({
    resolver: zodResolver(ApplySchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      heardAbout: "",
      motivation: "",
      linkedinUrl: "",
      githubUrl: "",
      portfolioUrl: "",
    },
  });

  // tRPC mutation to get signed upload URL
  const generateUploadUrl = trpc.storage.generateUploadUrl.useMutation();
  // tRPC mutation to create job application
  const createApplication = trpc.applicants.createApplication.useMutation();

  const onApplySubmit = async (values: ApplyValues) => {
    if (!resumeFile) {
      toast.error("Please upload your resume/CV before submitting.");
      return;
    }

    try {
      // Create a unique file name in the bucket
      const extension = resumeFile.name.split(".").pop();
      const fileName = `job-applications/${jobId}/${uuidv4()}.${extension}`;

      // Get signed upload URL
      const { uploadUrl, publicUrl } = await generateUploadUrl.mutateAsync({
        fileName,
        contentType: resumeFile.type || "application/octet-stream",
      });

      // Upload the file via PUT request
      await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": resumeFile.type || "application/octet-stream",
        },
        body: resumeFile,
      });

      // Build motivation with optional portfolio URL appended (no backend field required)
      const finalMotivation = values.portfolioUrl ? `${values.motivation}\n\nPortfolio: ${values.portfolioUrl}` : values.motivation;

      // Submit application details to backend
      await createApplication.mutateAsync({
        jobId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        heardAbout: values.heardAbout,
        motivation: finalMotivation,
        linkedinUrl: values.linkedinUrl || "",
        githubUrl: values.githubUrl || "",
        resumeFileName: resumeFile.name,
        resumeFileUrl: publicUrl,
      });

      posthog?.capture("job_application_submitted", {
        jobId,
        ...values,
      });

      toast.success("Application submitted successfully!");
      setStep("success");

      onSuccess?.();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Failed to submit application. Please try again.");
    }
  };

  const showGithubField = job?.id === "full-stack-developer" || job?.id === "design-engineer";

  return (
    <DialogContent size="3xl" className="p-0 max-w-3xl overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
      {step === "apply" && (
        <DialogHeader>
          <DialogTitle>Apply for {job?.title || "this role"}</DialogTitle>
          <DialogDescription>Fill in your details and upload your resume/CV.</DialogDescription>
        </DialogHeader>
      )}
      {step === "success" && (
        <DialogHeader>
          <DialogTitle>Application Received</DialogTitle>
          <DialogDescription>Thank you for applying! We'll get back to you soon.</DialogDescription>
        </DialogHeader>
      )}

      <div className="flex flex-col justify-between h-full">
        <div className="flex flex-col">
          {step === "apply" && (
            <Form {...applyForm}>
              <form onSubmit={applyForm.handleSubmit(onApplySubmit)} className="space-y-6 p-5">
                <div className="flex gap-4">
                  <FormField
                    control={applyForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Firstname" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={applyForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Lastname" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={applyForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="email@example.com" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* How did you hear about us */}
                <FormField
                  control={applyForm.control}
                  name="heardAbout"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How did you hear about us?</FormLabel>

                      <FormControl>
                        <Input placeholder="e.g. Twitter, LinkedIn, Friend, etc." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Motivation */}
                <FormField
                  control={applyForm.control}
                  name="motivation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Motivation</FormLabel>
                      <FormDescription>Tell us why you want to join Promptwatch, just a few sentences is fine.</FormDescription>
                      <FormControl>
                        <Textarea rows={4} className="h-20" placeholder="Tell us why you want to join Promptwatch" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* URLs */}
                <div className="flex gap-4">
                  <FormField
                    control={applyForm.control}
                    name="linkedinUrl"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>LinkedIn URL</FormLabel>
                        <FormControl>
                          <Input placeholder="https://linkedin.com/in/username" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {showGithubField && (
                    <FormField
                      control={applyForm.control}
                      name="githubUrl"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel>GitHub URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://github.com/username" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                {/* Portfolio website */}
                <FormField
                  control={applyForm.control}
                  name="portfolioUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Portfolio website (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://your-portfolio.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Resume upload */}
                <div className="space-y-2">
                  <label htmlFor="resume-upload" className="block text-sm font-medium">
                    Your resume/CV (PDF preferred)
                  </label>
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" onClick={() => document.getElementById("resume-upload")?.click()}>
                      Select File
                    </Button>
                    {resumeFile && <span className="text-sm text-muted-foreground">{resumeFile.name}</span>}
                  </div>
                  <input
                    id="resume-upload"
                    type="file"
                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setResumeFile(file);
                    }}
                  />
                </div>
              </form>
            </Form>
          )}

          {step === "success" && (
            <div className="p-5 mt-10 mb-12 flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-green-100 p-3 w-16 h-16 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold">Thank you!</h3>
              <p className="text-muted-foreground">Your application has been received.</p>
            </div>
          )}
        </div>

        {step === "apply" && (
          <DialogFooter className="flex flex-row-reverse w-full justify-end p-5 mt-auto">
            <Button
              onClick={applyForm.handleSubmit(onApplySubmit)}
              className="w-full"
              disabled={generateUploadUrl.isPending || createApplication.isPending}
            >
              {(generateUploadUrl.isPending || createApplication.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {generateUploadUrl.isPending || createApplication.isPending ? "Submitting..." : "Submit Application"}
            </Button>
          </DialogFooter>
        )}
      </div>

      {/* Removed right-hand visual column for full-width form */}
    </DialogContent>
  );
};

export { ApplyModal };
